import { useStorage } from '@vueuse/core'
import { acceptHMRUpdate } from 'pinia'

/**
 * Store for managing calendar category permissions with localStorage persistence
 * Checks if the current user can modify events based on calendar category ownership
 * and supervisor relationships
 */
export const useCalendarPermissionsStore = defineStore('calendarPermissionsStore', () => {
  const userStore = useUserStore()

  // Persisted state in localStorage
  const allowedCategoryIds = ref(
    useStorage('calendar_allowed_categories', [] as number[], localStorage)
  )
  const isInitialized = ref(useStorage('calendar_permissions_initialized', false, localStorage))
  const lastUserId = ref(useStorage('calendar_permissions_user_id', 0, localStorage))

  // Runtime-only state
  const isLoading = ref(false)

  /**
   * Fetches allowed calendar categories for the current user
   * Includes:
   * - Categories owned by the current user
   * - Categories owned by users supervised by the current user
   *
   * Uses caching: skips fetch if already initialized for the current user
   */
  const fetchPermissions = async () => {
    if (!userStore.user?.id) {
      console.warn('[CalendarPermissionsStore] No user ID available')
      return
    }

    // Skip if already initialized for this user
    if (isInitialized.value && lastUserId.value === userStore.user.id) {
      console.log('[CalendarPermissionsStore] ✅ Using cached permissions')
      return
    }

    isLoading.value = true
    try {
      console.log('[CalendarPermissionsStore] 🔄 Fetching permissions...')

      // Use the reusable composable to fetch supervised users
      const { fetchSupervisedUsers } = useSupervisedUsers()
      const supervisedUserIds = await fetchSupervisedUsers()

      // Include current user + supervised users
      const allUserIds = [userStore.user.id, ...supervisedUserIds]

      // Fetch calendar categories for all these users
      await userStore.fetchCalendarCategories(allUserIds)

      // Store allowed category IDs
      allowedCategoryIds.value = userStore.calendarCategories.map((c: any) => c.id)
      isInitialized.value = true
      lastUserId.value = userStore.user.id

      console.log(
        `[CalendarPermissionsStore] ✅ Cached ${allowedCategoryIds.value.length} allowed categories for user ${userStore.user.id}`
      )
    } catch (error) {
      console.error('[CalendarPermissionsStore] ❌ Error fetching calendar permissions:', error)
      allowedCategoryIds.value = []
      isInitialized.value = false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Checks if the current user can modify an event with the given category ID
   * @param categoryId - The calendar category ID to check
   * @returns true if user can modify, false otherwise
   *
   * Optimistic approach: returns true while loading or not initialized
   */
  const canModifyCategory = (categoryId: number | null | undefined): boolean => {
    if (!categoryId) return true

    if (isLoading.value) return true

    if (!isInitialized.value) return true

    return allowedCategoryIds.value.includes(categoryId)
  }

  /**
   * Checks if the current user can modify an event
   * @param event - Calendar event object with category property
   * @returns true if user can modify, false otherwise
   */
  const canModifyEvent = (event: { category?: { id?: number } } | null | undefined): boolean => {
    if (!event) return true
    return canModifyCategory(event.category?.id)
  }

  /**
   * Clears all cached permissions
   * Should be called on logout
   */
  const clearPermissions = () => {
    console.log('[CalendarPermissionsStore] 🧹 Clearing permissions cache')
    allowedCategoryIds.value = []
    isInitialized.value = false
    lastUserId.value = 0
  }

  /**
   * Checks if permissions have been loaded for any user
   */
  const hasPermissions = computed(() => {
    return isInitialized.value && allowedCategoryIds.value.length > 0
  })

  return {
    // State (readonly for external consumers)
    allowedCategoryIds: readonly(allowedCategoryIds),
    isInitialized: readonly(isInitialized),
    isLoading: readonly(isLoading),
    hasPermissions,

    // Actions
    fetchPermissions,
    canModifyCategory,
    canModifyEvent,
    clearPermissions
  }
})

// Enable HMR (Hot Module Replacement)
if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCalendarPermissionsStore, import.meta.hot))
}
